import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { flattenToAppURL } from '@plone/volto/helpers';
import AppNav from '../AppNav/AppNav';
import JobTitleField from '../JobTitleField/JobTitleField';
import MatterTitleField from '../MatterTitleField/MatterTitleField';
import {
  formatMatterReferenceMessage,
  formatMatterTitleMessage,
  formatTokenMessage,
  sharedMessages,
} from '../../utils/uiMessages';

const STORAGE_KEY = 'pm.job_form_defaults';
const LEGACY_STORAGE_KEY = 'pm.job_capture_defaults';
const CLOSED_STATUSES = new Set(['done', 'cancelled']);

const initialForm = {
  matter_path: '',
  office_action_path: '',
  title: '',
  job_kind: '',
  status: 'triage',
  incoming_date: null,
  internal_due_date: '',
  legal_due_date: '',
  client_due_date: '',
  legal_due_buffer_days: 2,
  estimated_hours: 1,
  remaining_hours: '',
  priority: 'medium',
  notes: '',
};

const FOLLOW_UP_PRESETS = [
  {
    id: 'oa_report',
    title: 'Report OA to client',
    preferredJobKinds: ['Reporting to the client', 'OA reporting'],
    status: 'not_started',
  },
  {
    id: 'client_instructions',
    title: 'Obtain client instructions',
    preferredJobKinds: ['Client communication', 'Others'],
    status: 'waiting_on_client',
  },
  {
    id: 'client_questions',
    title: 'Answer client questions',
    preferredJobKinds: ['Client communication', 'Others'],
    status: 'waiting_on_client',
  },
  {
    id: 'pto_response',
    title: 'Prepare response to the PTO',
    preferredJobKinds: ['Prepare submission to the PTO', 'Prepare response to the PTO'],
    status: 'not_started',
  },
  {
    id: 'pto_filing',
    title: 'File response with the PTO',
    preferredJobKinds: ['Prepare submission to the PTO', 'File response with the PTO'],
    status: 'not_started',
  },
];

const TITLE_CLASSIFICATION_PRESETS = [
  {
    title: 'Application drafting',
    preferredJobKinds: ['Others', 'Application drafting'],
  },
  {
    title: 'OA reporting',
    preferredJobKinds: ['Reporting to the client', 'OA reporting'],
  },
  {
    title: 'Prepare response to the PTO',
    preferredJobKinds: ['Prepare submission to the PTO', 'Prepare response to the PTO'],
  },
  {
    title: 'Client communication',
    preferredJobKinds: ['Client communication', 'Others'],
  },
  {
    title: 'File response with the PTO',
    preferredJobKinds: ['Prepare submission to the PTO', 'File response with the PTO'],
  },
  {
    title: 'Research / analysis',
    preferredJobKinds: ['Others', 'Research / analysis'],
  },
];

function todayISODate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function createInitialForm(overrides = {}) {
  return {
    ...initialForm,
    incoming_date: todayISODate(),
    ...overrides,
  };
}

const initialMatterForm = {
  title: '',
  client_name: '',
  application_number: '',
  matter_reference_code: '',
  jurisdiction: '',
  matter_status: 'open',
  responsible_attorney: '',
  notes: '',
};

const messages = defineMessages({
  applicationNumberHelp: {
    id: 'Example: 特願2020-123456',
    defaultMessage: 'Example: 特願2020-123456',
  },
  businessDayBufferBeforeDeadline: {
    id: 'Business-day buffer before external deadline',
    defaultMessage: 'Business-day buffer before external deadline',
  },
  cancelNewMatter: {
    id: 'Cancel new matter',
    defaultMessage: 'Cancel new matter',
  },
  clientName: {
    id: 'Client name',
    defaultMessage: 'Client name',
  },
  createMatterAndUseIt: {
    id: 'Create matter and use it',
    defaultMessage: 'Create matter and use it',
  },
  createNewMatter: {
    id: 'Create new matter',
    defaultMessage: 'Create new matter',
  },
  creatingMatter: {
    id: 'Creating matter…',
    defaultMessage: 'Creating matter…',
  },
  currentPendingJobsForThisMatter: {
    id: 'Current pending jobs for this matter',
    defaultMessage: 'Current pending jobs for this matter',
  },
  deadlinePlanning: {
    id: 'Deadline planning',
    defaultMessage: 'Deadline planning',
  },
  deadlinePlanningNote: {
    id: 'Keep all timing fields visible so each new job starts with a complete deadline picture.',
    defaultMessage:
      'Keep all timing fields visible so each new job starts with a complete deadline picture.',
  },
  dueOn: {
    id: 'due {date}',
    defaultMessage: 'due {date}',
  },
  associatedWithJob: {
    id: 'Associated with {title}',
    defaultMessage: 'Associated with {title}',
  },
  associatedJobNote: {
    id: 'Create a new job under the same matter. The current job is kept here only as reference context.',
    defaultMessage:
      'Create a new job under the same matter. The current job is kept here only as reference context.',
  },
  jobCreatedAt: {
    id: 'Job created at {url}',
    defaultMessage: 'Job created at {url}',
  },
  jobDetailsSection: {
    id: 'Job details',
    defaultMessage: 'Job details',
  },
  jobDetailsSectionNote: {
    id: 'Choose the job title, job kind, current state, and priority together.',
    defaultMessage:
      'Choose the job title, job kind, current state, and priority together.',
  },
  jobKindSettingsFailed: {
    id: 'Job kind settings failed with status {status}',
    defaultMessage: 'Job kind settings failed with status {status}',
  },
  matterCreatedAndSelected: {
    id: 'Matter {reference} created and selected.',
    defaultMessage: 'Matter {reference} created and selected.',
  },
  matterSelection: {
    id: 'Matter selection',
    defaultMessage: 'Matter selection',
  },
  matterSelectionNote: {
    id: 'Choose the matter first so the job inherits the correct case context.',
    defaultMessage:
      'Choose the matter first so the job inherits the correct case context.',
  },
  matterFormatHint: {
    id: 'Format: reference code: matter title',
    defaultMessage: 'Format: reference code: matter title',
  },
  matterLookupFailed: {
    id: 'Matter lookup failed with status {status}',
    defaultMessage: 'Matter lookup failed with status {status}',
  },
  noOpenJobs: {
    id: 'No open jobs',
    defaultMessage: 'No open jobs',
  },
  openJobsCount: {
    id: '{count} open jobs',
    defaultMessage: '{count} open jobs',
  },
  openExtraFieldsNote: {
    id: 'Open extra fields only when you need status, notes, or buffer settings.',
    defaultMessage:
      'Open extra fields only when you need status, notes, or buffer settings.',
  },
  preloadFromCurrentJob: {
    id: 'Copied legal/client deadlines, priority, and OA link from the current job. Adjust the next internal deadline for this specific step.',
    defaultMessage:
      'Copied legal/client deadlines, priority, and OA link from the current job. Adjust the next internal deadline for this specific step.',
  },
  overrideClassification: {
    id: 'Override classification',
    defaultMessage: 'Override classification',
  },
  autoClassification: {
    id: 'Auto classification: {jobKind}',
    defaultMessage: 'Auto classification: {jobKind}',
  },
  pendingJobLookupFailed: {
    id: 'Pending job lookup failed with status {status}',
    defaultMessage: 'Pending job lookup failed with status {status}',
  },
  planningAndNotes: {
    id: 'Planning and notes',
    defaultMessage: 'Planning and notes',
  },
  planningAndNotesNote: {
    id: 'Estimated and remaining hours help Today and Overview show a realistic workload.',
    defaultMessage:
      'Estimated and remaining hours help Today and Overview show a realistic workload.',
  },
  registerJobNote: {
    id: 'Create a job under a matter, then set whichever deadline actually governs the work.',
    defaultMessage:
      'Create a job under a matter, then set whichever deadline actually governs the work.',
  },
  requestFailedWithStatus: {
    id: 'Request failed with status {status}',
    defaultMessage: 'Request failed with status {status}',
  },
  reuseDefaultsNote: {
    id: 'The form reuses your last priority, estimate, buffer, and job kind defaults.',
    defaultMessage:
      'The form reuses your last priority, estimate, buffer, and job kind defaults.',
  },
  selectPreset: {
    id: 'Select preset',
    defaultMessage: 'Select preset',
  },
  searchByMatterReferenceOrTitle: {
    id: 'Search by matter reference or matter title',
    defaultMessage: 'Search by matter reference or matter title',
  },
  setAtLeastOneDeadline: {
    id: 'Set at least one deadline. For opinion work, Internal working deadline or Client deadline may exist even when there is no legal deadline.',
    defaultMessage:
      'Set at least one deadline. For opinion work, Internal working deadline or Client deadline may exist even when there is no legal deadline.',
  },
  deriveInternalDeadline: {
    id: 'If only legal or client deadline is set, the system can derive the internal working deadline from the business-day buffer.',
    defaultMessage:
      'If only legal or client deadline is set, the system can derive the internal working deadline from the business-day buffer.',
  },
  recommendedStartTiming: {
    id: 'Recommended start timing is calculated from estimated hours and due dates after the job is saved.',
    defaultMessage:
      'Recommended start timing is calculated from estimated hours and due dates after the job is saved.',
  },
  shortHours: {
    id: '{hours}h',
    defaultMessage: '{hours}h',
  },
  showExtraFields: {
    id: 'Show extra fields',
    defaultMessage: 'Show extra fields',
  },
  hideExtraFields: {
    id: 'Hide extra fields',
    defaultMessage: 'Hide extra fields',
  },
  useFullFormNote: {
    id: 'All planning fields are shown here so you can register a complete job in one pass.',
    defaultMessage:
      'All planning fields are shown here so you can register a complete job in one pass.',
  },
});

export default function PatentJobForm() {
  const intl = useIntl();
  const token = useSelector((state) => state.userSession?.token);
  const queryParams = useMemo(() => {
    if (typeof window === 'undefined') {
      return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
  }, []);
  const presetMatterPath = normalizeContentPath(queryParams.get('matter_path') || '');
  const sourceJobPath = normalizeContentPath(queryParams.get('source_job_path') || '');
  const [formDefaults, setFormDefaults] = useState(() => loadFormDefaults());
  const [form, setForm] = useState({
    ...createInitialForm(),
    priority: loadFormDefaults().priority || initialForm.priority,
    estimated_hours:
      loadFormDefaults().estimated_hours || initialForm.estimated_hours,
    legal_due_buffer_days:
      loadFormDefaults().legal_due_buffer_days ??
      initialForm.legal_due_buffer_days,
    job_kind: loadFormDefaults().job_kind || initialForm.job_kind,
  });
  const [sourceJob, setSourceJob] = useState(null);
  const [matterForm, setMatterForm] = useState(initialMatterForm);
  const [matters, setMatters] = useState([]);
  const [matterQuery, setMatterQuery] = useState('');
  const [jobKindOptions, setJobKindOptions] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMatters, setLoadingMatters] = useState(true);
  const [loadingPendingJobs, setLoadingPendingJobs] = useState(false);
  const [creatingMatter, setCreatingMatter] = useState(false);
  const [showNewMatter, setShowNewMatter] = useState(false);
  const [showJobKindOverride, setShowJobKindOverride] = useState(false);
  const [error, setError] = useState('');
  const [createdUrl, setCreatedUrl] = useState('');
  const [matterMessage, setMatterMessage] = useState('');

  function authHeaders(extra = {}) {
    return token
      ? {
          ...extra,
          Authorization: `Bearer ${token}`,
        }
      : extra;
  }

  useEffect(() => {
    let active = true;

    async function loadMatters() {
      try {
        const items = await fetchMatterOptions(authHeaders, intl);

        if (active) {
          setMatters(items);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setLoadingMatters(false);
        }
      }
    }

    loadMatters();
    return () => {
      active = false;
    };
  }, [intl, token]);

  useEffect(() => {
    let active = true;

    async function loadSourceJob() {
      if (!sourceJobPath) {
        if (active) {
          setSourceJob(null);
        }
        return;
      }

      try {
        const response = await fetch(`/++api++${sourceJobPath}`, {
          headers: authHeaders({ Accept: 'application/json' }),
        });
        if (!response.ok) {
          throw new Error(
            intl.formatMessage(messages.requestFailedWithStatus, {
              status: response.status,
            }),
          );
        }
        const payload = await response.json();
        const normalized = normalizeSourceJob(payload);
        if (!active) {
          return;
        }
        setSourceJob(normalized);
        if (hasMeaningfulMatterReference(normalized.matter_reference_code)) {
          setMatterQuery((current) =>
            current ||
            formatMatterOptionLabelFromValues(
              normalized.matter_reference_code,
              normalized.matter_title,
              intl,
            ),
          );
        }
        setForm((current) => ({
          ...current,
          matter_path: current.matter_path || normalized.matter_path,
          office_action_path:
            current.office_action_path || normalized.office_action_path,
          legal_due_date: current.legal_due_date || normalized.legal_due_date,
          client_due_date: current.client_due_date || normalized.client_due_date,
          legal_due_buffer_days:
            normalized.legal_due_buffer_days ?? current.legal_due_buffer_days,
          priority: normalized.priority || current.priority || initialForm.priority,
        }));
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      }
    }

    loadSourceJob();
    return () => {
      active = false;
    };
  }, [intl, sourceJobPath, token]);

  useEffect(() => {
    const nextMatterPath = form.matter_path || presetMatterPath || sourceJob?.matter_path;
    if (!nextMatterPath || !matters.length) {
      return;
    }
    const selected = matters.find((matter) => matter.path === nextMatterPath);
    if (!selected) {
      return;
    }
    setForm((current) =>
      current.matter_path === nextMatterPath
        ? current
        : { ...current, matter_path: nextMatterPath },
    );
    setMatterQuery((current) =>
      !current || isPlaceholderMatterLabel(current, intl)
        ? formatMatterOptionLabel(selected, intl)
        : current,
    );
  }, [form.matter_path, intl, matters, presetMatterPath, sourceJob]);

  useEffect(() => {
    let active = true;

    async function loadJobKinds() {
      try {
        const response = await fetch('/++api++/@job-kind-settings', {
          headers: authHeaders({ Accept: 'application/json' }),
        });
        if (!response.ok) {
          throw new Error(
            intl.formatMessage(messages.jobKindSettingsFailed, {
              status: response.status,
            }),
          );
        }
        const payload = await response.json();
        const items = payload.items || [];
        if (active) {
          setJobKindOptions(items);
          setForm((current) => ({
            ...current,
            job_kind: current.job_kind || formDefaults.job_kind || items[0] || '',
          }));
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      }
    }

    loadJobKinds();
    return () => {
      active = false;
    };
  }, [formDefaults.job_kind, intl, token]);

  useEffect(() => {
    let active = true;

    async function loadPendingJobs() {
      if (!form.matter_path || showNewMatter) {
        if (active) {
          setPendingJobs([]);
          setLoadingPendingJobs(false);
        }
        return;
      }

      setLoadingPendingJobs(true);
      try {
        const query = new URLSearchParams({
          portal_type: 'Patent Job',
          b_size: '200',
          'path.query': form.matter_path,
          'path.depth': '1',
          metadata_fields: 'internal_due_date',
        });
        query.append('metadata_fields', 'status');
        query.append('metadata_fields', 'estimated_hours');
        query.append('metadata_fields', 'title');
        query.append('metadata_fields', 'getURL');
        const response = await fetch(`/++api++/@search?${query.toString()}`, {
          headers: authHeaders({ Accept: 'application/json' }),
        });
        if (!response.ok) {
          throw new Error(
            intl.formatMessage(messages.pendingJobLookupFailed, {
              status: response.status,
            }),
          );
        }
        const payload = await response.json();
        const items = (payload.items || [])
          .map(normalizePendingJobItem)
          .filter((item) => !CLOSED_STATUSES.has(item.status))
          .sort((left, right) => {
            const leftDue = left.internal_due_date || '9999-12-31';
            const rightDue = right.internal_due_date || '9999-12-31';
            return (
              leftDue.localeCompare(rightDue) || left.title.localeCompare(right.title)
            );
          });
        if (active) {
          setPendingJobs(items);
        }
      } catch (loadError) {
        if (active) {
          setPendingJobs([]);
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setLoadingPendingJobs(false);
        }
      }
    }

    loadPendingJobs();
    return () => {
      active = false;
    };
  }, [form.matter_path, intl, showNewMatter, token]);

  const filteredMatters = useMemo(() => {
    const query = matterQuery.trim().toLowerCase();
    if (!query) {
      return matters;
    }
    return matters.filter((matter) =>
      formatMatterOptionLabel(matter, intl).toLowerCase().includes(query),
    );
  }, [intl, matterQuery, matters]);

  const autoJobKind = useMemo(
    () =>
      resolveTitleDrivenJobKind(
        form.title,
        jobKindOptions,
        formDefaults.job_kind || '',
      ),
    [form.title, formDefaults.job_kind, jobKindOptions],
  );
  const showJobKindField = showJobKindOverride || !autoJobKind;

  useEffect(() => {
    if (!autoJobKind || showJobKindOverride) {
      return;
    }
    setForm((current) =>
      current.job_kind === autoJobKind
        ? current
        : { ...current, job_kind: autoJobKind },
    );
  }, [autoJobKind, showJobKindOverride]);

  async function refreshMatters() {
    const items = await fetchMatterOptions(authHeaders, intl);
    setMatters(items);
    return items;
  }

  function onToggleNewMatter() {
    setShowNewMatter((current) => {
      const next = !current;
      if (next) {
        setMatterForm((existing) => {
          if (String(existing.matter_reference_code || '').trim()) {
            return existing;
          }
          const seededReference = deriveMatterReferenceDraft(matterQuery);
          return seededReference
            ? { ...existing, matter_reference_code: seededReference }
            : existing;
        });
      }
      return next;
    });
  }

  async function onCreateMatter(event) {
    event.preventDefault();
    setCreatingMatter(true);
    setError('');
    setMatterMessage('');

    try {
      const normalizedMatterReferenceCode = normalizeMatterReference(
        matterForm.matter_reference_code,
      );
      const response = await fetch('/++api++/', {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          '@type': 'Matter',
          ...matterForm,
          matter_reference_code: normalizedMatterReferenceCode,
          matter_reference: normalizedMatterReferenceCode,
        }),
      });

      if (!response.ok) {
        throw new Error(await extractError(response, intl));
      }

      const payload = await response.json();
      const path = flattenToAppURL(payload['@id'] || '');
      const items = await refreshMatters();
      const selected =
        items.find((item) => item.path === path) || {
          title: matterForm.title,
          matterReferenceCode: normalizedMatterReferenceCode,
          path,
        };

      setForm((current) => ({ ...current, matter_path: selected.path }));
      setMatterQuery(formatMatterOptionLabel(selected, intl));
      setMatterForm(initialMatterForm);
      setShowNewMatter(false);
      setMatterMessage(
        intl.formatMessage(messages.matterCreatedAndSelected, {
          reference: formatMatterReferenceMessage(
            intl,
            selected.matterReferenceCode,
          ),
        }),
      );
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setCreatingMatter(false);
    }
  }

  function onTitleChange(value) {
    setForm((current) => ({ ...current, title: value }));
  }

  function onToggleJobKindOverride() {
    setShowJobKindOverride((current) => {
      const next = !current;
      if (!next && autoJobKind) {
        setForm((formState) => ({ ...formState, job_kind: autoJobKind }));
      }
      return next;
    });
  }

  async function onSubmit(event) {
    event.preventDefault();
    setLoading(true);
    setError('');
    setCreatedUrl('');

    try {
      const normalizedBufferDays = Number(form.legal_due_buffer_days);
      const normalizedEstimatedHours = String(form.estimated_hours ?? '').trim();
      const normalizedRemainingHours = String(form.remaining_hours ?? '').trim();
      const normalizedJobKind = resolveSubmittedJobKind(
        form.job_kind,
        jobKindOptions,
        formDefaults.job_kind,
      );
      const response = await fetch(`/++api++${form.matter_path}`, {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          '@type': 'Patent Job',
          title: form.title,
          job_kind: normalizedJobKind,
          status: form.status || 'triage',
          incoming_date: form.incoming_date || null,
          internal_due_date: form.internal_due_date || null,
          legal_due_date: form.legal_due_date || null,
          client_due_date: form.client_due_date || null,
          office_action_path: form.office_action_path || null,
          legal_due_buffer_days: Number.isNaN(normalizedBufferDays)
            ? 0
            : normalizedBufferDays,
          estimated_hours: normalizedEstimatedHours || '0',
          remaining_hours: normalizedRemainingHours || null,
          priority: form.priority || 'medium',
          notes: form.notes,
        }),
      });

      if (!response.ok) {
        throw new Error(await extractError(response, intl));
      }

      const payload = await response.json();
      const nextDefaults = {
        priority: form.priority || 'medium',
        estimated_hours: normalizedEstimatedHours || '1',
        legal_due_buffer_days: Number.isNaN(normalizedBufferDays)
          ? 2
          : normalizedBufferDays,
        job_kind: normalizedJobKind,
      };
      saveFormDefaults(nextDefaults);
      setFormDefaults(nextDefaults);
      setCreatedUrl(payload['@id'] || '');
      setForm(createInitialForm({
        matter_path: form.matter_path,
        office_action_path: form.office_action_path,
        job_kind: nextDefaults.job_kind,
        priority: nextDefaults.priority,
        estimated_hours: nextDefaults.estimated_hours,
        legal_due_buffer_days: nextDefaults.legal_due_buffer_days,
        legal_due_date: sourceJob?.legal_due_date || '',
        client_due_date: sourceJob?.client_due_date || '',
      }));
      if (!sourceJob) {
        setMatterQuery('');
      }
      if (payload['@id']) {
        window.location.assign(
          `/job-editor?path=${encodeURIComponent(
            normalizeContentPath(payload['@id']),
          )}`,
        );
        return;
      }
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="patent-entry">
      <section className="patent-entry__shell">
        <AppNav currentPath="/jobs" />
        <header className="patent-entry__hero">
          <p className="patent-entry__eyebrow">
            {intl.formatMessage(sharedMessages.patentManager)}
          </p>
          <h1>{intl.formatMessage(sharedMessages.registerJob)}</h1>
          <p className="patent-entry__note">
            {intl.formatMessage(messages.registerJobNote)}
          </p>
          <div className="patent-entry__actions patent-entry__actions--row">
            <Link className="patent-entry__ghost" to="/overview">
              {intl.formatMessage(sharedMessages.backToOverview)}
            </Link>
          </div>
        </header>

        <form className="patent-entry__form patent-entry__form--stacked" onSubmit={onSubmit}>
          <SectionCard
            title={intl.formatMessage(messages.matterSelection)}
            note={intl.formatMessage(messages.matterSelectionNote)}
          >
            <div className="patent-entry__section-grid">
              <label className="patent-entry__field patent-entry__field--wide">
                <span className="patent-entry__label">
                  {intl.formatMessage(sharedMessages.matter)}
                </span>
                <input
                  className="patent-entry__input"
                  list="patent-manager-matters"
                  value={matterQuery}
                  onChange={(event) => {
                    const value = event.target.value;
                    setMatterQuery(value);
                    const matchedMatter = matters.find(
                      (matter) => formatMatterOptionLabel(matter, intl) === value,
                    );
                    setForm((current) => ({
                      ...current,
                      matter_path: matchedMatter ? matchedMatter.path : '',
                    }));
                  }}
                  disabled={loadingMatters || !matters.length}
                  placeholder={intl.formatMessage(messages.searchByMatterReferenceOrTitle)}
                  required
                />
                <datalist id="patent-manager-matters">
                  {filteredMatters.map((matter) => (
                    <option
                      key={matter.path}
                      value={formatMatterOptionLabel(matter, intl)}
                    />
                  ))}
                </datalist>
                <span className="patent-entry__help">
                  {intl.formatMessage(messages.matterFormatHint)}
                </span>
              </label>
            </div>

            <div className="patent-entry__actions patent-entry__actions--row">
              <button
                className="patent-entry__ghost"
                type="button"
                onClick={onToggleNewMatter}
              >
                {showNewMatter
                  ? intl.formatMessage(messages.cancelNewMatter)
                  : intl.formatMessage(messages.createNewMatter)}
              </button>
              {matterMessage ? (
                <span className="patent-entry__note">{matterMessage}</span>
              ) : null}
            </div>

            {showNewMatter ? (
              <div className="patent-entry__section-grid">
                <TextField
                  label={intl.formatMessage(sharedMessages.matterReferenceCode)}
                  value={matterForm.matter_reference_code}
                  onChange={(value) =>
                    setMatterForm((current) => ({
                      ...current,
                      matter_reference_code: value,
                    }))
                  }
                />
                <MatterTitleField
                  value={matterForm.title}
                  onChange={(value) =>
                    setMatterForm((current) => ({ ...current, title: value }))
                  }
                  required
                />
                <TextField
                  label={intl.formatMessage(messages.clientName)}
                  value={matterForm.client_name}
                  onChange={(value) =>
                    setMatterForm((current) => ({ ...current, client_name: value }))
                  }
                />
                <TextField
                  label={intl.formatMessage(sharedMessages.applicationNumber)}
                  value={matterForm.application_number}
                  onChange={(value) =>
                    setMatterForm((current) => ({
                      ...current,
                      application_number: value,
                    }))
                  }
                  helpText={intl.formatMessage(messages.applicationNumberHelp)}
                />
                <TextField
                  label={intl.formatMessage(sharedMessages.jurisdiction)}
                  value={matterForm.jurisdiction}
                  onChange={(value) =>
                    setMatterForm((current) => ({ ...current, jurisdiction: value }))
                  }
                />
                <SelectField
                  label={intl.formatMessage(sharedMessages.matterStatus)}
                  value={matterForm.matter_status}
                  onChange={(value) =>
                    setMatterForm((current) => ({ ...current, matter_status: value }))
                  }
                  options={[
                    'open',
                    'awaiting_instruction',
                    'active_prosecution',
                    'allowed',
                    'abandoned',
                    'granted',
                    'closed',
                  ]}
                />
                <div className="patent-entry__field patent-entry__field--wide">
                  <button
                    className="patent-entry__submit"
                    type="button"
                    onClick={onCreateMatter}
                    disabled={creatingMatter || !matterForm.title.trim()}
                  >
                    {creatingMatter
                      ? intl.formatMessage(messages.creatingMatter)
                      : intl.formatMessage(messages.createMatterAndUseIt)}
                  </button>
                </div>
              </div>
            ) : null}

            {form.matter_path && !showNewMatter ? (
              <section className="patent-entry__pending-jobs">
                <div className="patent-entry__pending-jobs-head">
                  <strong>
                    {intl.formatMessage(messages.currentPendingJobsForThisMatter)}
                  </strong>
                  {loadingPendingJobs ? (
                    <span className="patent-entry__note">
                      {intl.formatMessage(sharedMessages.loading)}
                    </span>
                  ) : pendingJobs.length ? (
                    <span className="patent-entry__note">
                      {intl.formatMessage(messages.openJobsCount, {
                        count: pendingJobs.length,
                      })}
                    </span>
                  ) : (
                    <span className="patent-entry__note">
                      {intl.formatMessage(messages.noOpenJobs)}
                    </span>
                  )}
                </div>
                {pendingJobs.length ? (
                  <ul className="patent-entry__pending-jobs-list">
                    {pendingJobs.map((item) => (
                      <li
                        key={`${item.url}-${item.title}`}
                        className="patent-entry__pending-job"
                      >
                        <Link
                          className="patent-entry__pending-job-title"
                          to={`/job-editor?path=${encodeURIComponent(
                            normalizeContentPath(item.url),
                          )}`}
                        >
                          {item.title}
                        </Link>
                        <span className="patent-entry__pending-job-meta">
                          {formatJobMeta(intl, item)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ) : null}
          </SectionCard>

          {sourceJob ? (
            <SectionCard
              title={intl.formatMessage(messages.associatedWithJob, {
                title: sourceJob.title,
              })}
              note={intl.formatMessage(messages.associatedJobNote)}
              muted
            >
              <p className="patent-entry__note">
                {intl.formatMessage(messages.preloadFromCurrentJob)}
              </p>
              <div className="patent-entry__source-summary">
                <span className="patent-entry__context-chip">
                  {intl.formatMessage(sharedMessages.matterRef)}:{' '}
                  {formatMatterReferenceMessage(intl, sourceJob.matter_reference_code)}
                </span>
                <Link
                  className="patent-entry__ghost"
                  to={`/job-editor?path=${encodeURIComponent(sourceJob.path)}`}
                >
                  {intl.formatMessage(sharedMessages.backToJob)}
                </Link>
              </div>
            </SectionCard>
          ) : null}

          <SectionCard
            title={intl.formatMessage(messages.jobDetailsSection)}
            note={intl.formatMessage(messages.jobDetailsSectionNote)}
          >
            <div className="patent-entry__section-grid">
              <JobTitleField
                value={form.title}
                onChange={onTitleChange}
                options={jobKindOptions}
                required
              />
              <SelectField
                label={intl.formatMessage(sharedMessages.status)}
                value={form.status}
                onChange={(value) =>
                  setForm((current) => ({ ...current, status: value }))
                }
                options={[
                  'triage',
                  'not_started',
                  'in_progress',
                  'queued_for_review',
                  'waiting_on_client',
                  'waiting_on_foreign_associate',
                  'ready_to_file',
                  'blocked',
                  'done',
                  'cancelled',
                ]}
              />
              <SelectField
                label={intl.formatMessage(sharedMessages.priority)}
                value={form.priority}
                onChange={(value) =>
                  setForm((current) => ({ ...current, priority: value }))
                }
                options={['low', 'medium', 'high', 'critical']}
              />
            </div>
            <div className="patent-entry__actions patent-entry__actions--row">
              {autoJobKind && !showJobKindField ? (
                <span className="patent-entry__note">
                  {intl.formatMessage(messages.autoClassification, {
                    jobKind: formatTokenMessage(intl, autoJobKind),
                  })}
                </span>
              ) : null}
              {autoJobKind ? (
                <button
                  className="patent-entry__ghost"
                  type="button"
                  onClick={onToggleJobKindOverride}
                >
                  {intl.formatMessage(messages.overrideClassification)}
                </button>
              ) : null}
            </div>
            {showJobKindField ? (
              <div className="patent-entry__section-grid">
                <SelectField
                  label={intl.formatMessage(sharedMessages.jobKind)}
                  value={form.job_kind}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, job_kind: value }))
                  }
                  options={mergeLegacyOption(jobKindOptions, form.job_kind)}
                />
              </div>
            ) : null}
          </SectionCard>

          <SectionCard
            title={intl.formatMessage(messages.deadlinePlanning)}
            note={intl.formatMessage(messages.deadlinePlanningNote)}
          >
            <div className="patent-entry__section-grid patent-entry__section-grid--dates">
              <DateField
                label={intl.formatMessage(sharedMessages.incomingDate)}
                value={form.incoming_date}
                onChange={(value) =>
                  setForm((current) => ({ ...current, incoming_date: value }))
                }
              />
              <DateField
                label={intl.formatMessage(sharedMessages.clientDeadline)}
                value={form.client_due_date}
                onChange={(value) =>
                  setForm((current) => ({ ...current, client_due_date: value }))
                }
              />
              <DateField
                label={intl.formatMessage(sharedMessages.legalDeadline)}
                value={form.legal_due_date}
                onChange={(value) =>
                  setForm((current) => ({ ...current, legal_due_date: value }))
                }
              />
              <DateField
                label={intl.formatMessage(sharedMessages.internalWorkingDeadline)}
                value={form.internal_due_date}
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    internal_due_date: value,
                  }))
                }
              />
              <NumberField
                label={intl.formatMessage(messages.businessDayBufferBeforeDeadline)}
                value={form.legal_due_buffer_days}
                min="0"
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    legal_due_buffer_days: value,
                  }))
                }
              />
            </div>
            <div className="patent-entry__quick-note">
              <p className="patent-entry__note">
                {intl.formatMessage(messages.setAtLeastOneDeadline)}
              </p>
              <p className="patent-entry__note">
                {intl.formatMessage(messages.deriveInternalDeadline)}
              </p>
              <p className="patent-entry__note">
                {intl.formatMessage(messages.recommendedStartTiming)}
              </p>
            </div>
          </SectionCard>

          <SectionCard
            title={intl.formatMessage(messages.planningAndNotes)}
            note={intl.formatMessage(messages.planningAndNotesNote)}
          >
            <div className="patent-entry__section-grid">
              <NumberField
                label={intl.formatMessage(sharedMessages.estimatedHours)}
                value={form.estimated_hours}
                min="0"
                step="0.5"
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    estimated_hours: value,
                  }))
                }
              />
              <NumberField
                label={intl.formatMessage(sharedMessages.remainingHours)}
                value={form.remaining_hours}
                min="0"
                step="0.5"
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    remaining_hours: value,
                  }))
                }
              />
            </div>
            <TextAreaField
              label={intl.formatMessage(sharedMessages.notes)}
              value={form.notes}
              onChange={(value) =>
                setForm((current) => ({ ...current, notes: value }))
              }
            />
            <div className="patent-entry__quick-note">
              <p className="patent-entry__note">
                {intl.formatMessage(messages.reuseDefaultsNote)}
              </p>
              <p className="patent-entry__note">
                {intl.formatMessage(messages.useFullFormNote)}
              </p>
            </div>
          </SectionCard>

          {error ? <p className="patent-entry__error">{error}</p> : null}
          {createdUrl ? (
            <p className="patent-entry__success">
              {intl.formatMessage(messages.jobCreatedAt, {
                url: createdUrl,
              })}
            </p>
          ) : null}

          <div className="patent-entry__actions">
            <button
              className="patent-entry__submit"
              type="submit"
              disabled={
                loading ||
                loadingMatters ||
                !form.matter_path ||
                !form.title.trim() ||
                !hasAnyDeadline(form)
              }
            >
              {loading
                ? intl.formatMessage(sharedMessages.creating)
                : intl.formatMessage(sharedMessages.createJob)}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}

function TextField({ label, value, onChange, required = false, helpText = '' }) {
  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <input
        className="patent-entry__input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
      />
      {helpText ? <span className="patent-entry__help">{helpText}</span> : null}
    </label>
  );
}

function SectionCard({ title, note = '', children, muted = false }) {
  return (
    <section className={`patent-entry__card${muted ? ' patent-entry__card--muted' : ''}`}>
      <div className="patent-entry__section-header">
        <div>
          <h2>{title}</h2>
          {note ? <p className="patent-entry__note">{note}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function DateField({ label, value, onChange, required = false }) {
  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <input
        className="patent-entry__input"
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
      />
    </label>
  );
}

function NumberField({ label, value, onChange, min, step }) {
  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <input
        className="patent-entry__input"
        type="number"
        min={min}
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange }) {
  return (
    <label className="patent-entry__field patent-entry__field--wide">
      <span className="patent-entry__label">{label}</span>
      <textarea
        className="patent-entry__textarea"
        rows="5"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  const intl = useIntl();

  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <select
        className="patent-entry__input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {formatTokenMessage(intl, option)}
          </option>
        ))}
      </select>
    </label>
  );
}

async function extractError(response, intl) {
  try {
    const payload = await response.json();
    return (
      payload.message ||
      payload.error?.message ||
      intl.formatMessage(messages.requestFailedWithStatus, {
        status: response.status,
      })
    );
  } catch {
    return intl.formatMessage(messages.requestFailedWithStatus, {
      status: response.status,
    });
  }
}

function formatJobMeta(intl, item) {
  const parts = [formatTokenMessage(intl, item.status)];
  if (item.internal_due_date) {
    parts.push(
      intl.formatMessage(messages.dueOn, {
        date: item.internal_due_date,
      }),
    );
  }
  if (item.estimated_hours) {
    parts.push(
      intl.formatMessage(messages.shortHours, {
        hours: item.estimated_hours,
      }),
    );
  }
  return parts.join(' · ');
}

function formatMatterOptionLabel(matter, intl) {
  return formatMatterOptionLabelFromValues(
    matter.matterReferenceCode,
    matter.title,
    intl,
  );
}

function formatMatterOptionLabelFromValues(referenceCode, titleValue, intl) {
  const reference = formatMatterReferenceMessage(intl, referenceCode);
  const title = formatMatterTitleMessage(intl, titleValue);
  return `${reference}: ${title}`;
}

function hasMeaningfulMatterReference(referenceCode) {
  const reference = String(referenceCode || '').trim().toLowerCase();
  return Boolean(reference && reference !== 'not assigned');
}

function isPlaceholderMatterLabel(value, intl) {
  const notAssignedLabel = intl.formatMessage({ id: 'Not assigned', defaultMessage: 'Not assigned' });
  const trimmed = String(value || '').trim();
  return (
    trimmed === formatMatterOptionLabelFromValues('', '', intl) ||
    trimmed.startsWith(`${notAssignedLabel}: `)
  );
}

function normalizeMatterReference(value) {
  const trimmed = (value || '').trim();
  return trimmed || 'not assigned';
}

function deriveMatterReferenceDraft(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return '';
  }
  const separatorIndex = trimmed.indexOf(':');
  if (separatorIndex === -1) {
    return trimmed;
  }
  return trimmed.slice(0, separatorIndex).trim();
}

function normalizeContentPath(value) {
  return flattenToAppURL(value || '');
}

function normalizePendingJobItem(item) {
  return {
    title: item.title || '',
    url: item.getURL || item['@id'] || item.url || '',
    internal_due_date: item.internal_due_date || '',
    status: item.status?.token || item.status || 'not_started',
    estimated_hours: item.estimated_hours ?? 0,
  };
}

function normalizeSourceJob(payload) {
  return {
    path: normalizeContentPath(payload['@id'] || ''),
    title: payload.title || '',
    matter_path: normalizeContentPath(payload.parent?.['@id'] || ''),
    matter_reference_code:
      payload.parent?.matter_reference_code || payload.parent?.matter_reference || '',
    matter_title: payload.parent?.title || '',
    legal_due_date: payload.legal_due_date || '',
    client_due_date: payload.client_due_date || '',
    legal_due_buffer_days: payload.legal_due_buffer_days ?? 2,
    priority: payload.priority || 'medium',
    office_action_path: normalizeContentPath(payload.office_action_path || ''),
  };
}

async function fetchMatterOptions(authHeaders, intl) {
  const response = await fetch(
    '/++api++/@search?portal_type=Matter&sort_on=sortable_title&sort_order=ascending&b_size=200&metadata_fields=matter_reference_code&metadata_fields=matter_reference',
    { headers: authHeaders({ Accept: 'application/json' }) },
  );
  if (!response.ok) {
    throw new Error(
      intl.formatMessage(messages.matterLookupFailed, {
        status: response.status,
      }),
    );
  }
  const payload = await response.json();
  return (payload.items || []).map((item) => ({
    title: item.title || '',
    matterReferenceCode: item.matter_reference_code || item.matter_reference || '',
    path: flattenToAppURL(item['@id'] || ''),
  }));
}

function loadFormDefaults() {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw =
      window.localStorage.getItem(STORAGE_KEY) ||
      window.localStorage.getItem(LEGACY_STORAGE_KEY) ||
      '{}';
    const payload = JSON.parse(raw);
    return typeof payload === 'object' && payload ? payload : {};
  } catch {
    return {};
  }
}

function saveFormDefaults(defaults) {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
}

function hasAnyDeadline(form) {
  return Boolean(
    String(form.internal_due_date || '').trim() ||
      String(form.legal_due_date || '').trim() ||
      String(form.client_due_date || '').trim(),
  );
}

function mergeLegacyOption(options, currentValue) {
  const merged = [...(options || [])];
  if (currentValue && !merged.includes(currentValue)) {
    merged.push(currentValue);
  }
  return merged;
}

function resolveTitleDrivenJobKind(title, options, fallback = '') {
  const trimmed = String(title || '').trim();
  if (!trimmed) {
    return '';
  }
  const normalizedOptions = normalizeOptionList(options);
  if (normalizedOptions.includes(trimmed)) {
    return trimmed;
  }
  const preset = [...FOLLOW_UP_PRESETS, ...TITLE_CLASSIFICATION_PRESETS].find(
    (item) => item.title === trimmed,
  );
  if (!preset) {
    return '';
  }
  return resolvePresetJobKind(preset, options, fallback);
}

function resolveSubmittedJobKind(currentValue, options, defaultValue = '') {
  const normalizedOptions = normalizeOptionList(options);
  const trimmed = String(currentValue || '').trim();
  if (trimmed && normalizedOptions.includes(trimmed)) {
    return trimmed;
  }
  const matchedPreset = FOLLOW_UP_PRESETS.find((preset) =>
    preset.preferredJobKinds?.includes(trimmed),
  );
  if (matchedPreset) {
    return resolvePresetJobKind(matchedPreset, normalizedOptions, defaultValue);
  }
  return normalizedOptions[0] || defaultValue || '';
}

function resolvePresetJobKind(preset, options, fallback = '') {
  const normalizedOptions = normalizeOptionList(options);
  const preferred = preset?.preferredJobKinds || [];

  for (const candidate of preferred) {
    if (normalizedOptions.includes(candidate)) {
      return candidate;
    }
  }

  const foldedOptions = normalizedOptions.map((option) => ({
    raw: option,
    normalized: normalizeJobKindLabel(option),
  }));

  for (const candidate of preferred) {
    const normalizedCandidate = normalizeJobKindLabel(candidate);
    const matched = foldedOptions.find(
      (option) => option.normalized === normalizedCandidate,
    );
    if (matched) {
      return matched.raw;
    }
  }

  if (
    preset?.id === 'oa_report' &&
    foldedOptions.some((option) => option.normalized.includes('reporting'))
  ) {
    return foldedOptions.find((option) =>
      option.normalized.includes('reporting'),
    ).raw;
  }

  if (
    ['pto_response', 'pto_filing'].includes(preset?.id) &&
    foldedOptions.some((option) => option.normalized.includes('submission'))
  ) {
    return foldedOptions.find((option) =>
      option.normalized.includes('submission'),
    ).raw;
  }

  if (
    ['client_instructions', 'client_questions'].includes(preset?.id) &&
    foldedOptions.some(
      (option) =>
        option.normalized.includes('communication') ||
        option.normalized.includes('other'),
    )
  ) {
    return foldedOptions.find(
      (option) =>
        option.normalized.includes('communication') ||
        option.normalized.includes('other'),
    ).raw;
  }

  return fallback || normalizedOptions[0] || '';
}

function normalizeJobKindLabel(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ');
}

function normalizeOptionList(options) {
  return Array.from(
    new Set(
      (options || []).map((option) => String(option || '').trim()).filter(Boolean),
    ),
  );
}
